import { useState } from "react";
import {
  Aviso,
  Botao,
  Cabecalho,
  Campo,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Tabela,
} from "../componentes/ui";
import { DetalheDoAtivo } from "../componentes/ativos/DetalheDoAtivo";
import { FormularioDeAtivo } from "../componentes/ativos/FormularioDeAtivo";
import { mensagemDoConsole } from "../nucleo/console";
import { nomeDaOrganizacao, useOrganizacoesDoCadastro } from "../nucleo/cadastro";
import {
  CATEGORIAS_DE_ATIVO,
  ROTULO_DA_CATEGORIA,
  ROTULO_DO_STATUS,
  STATUS_DE_ATIVO,
  dataLegivel,
  estadoDoChipDoStatus,
  useAtivos,
} from "../nucleo/ativos";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";
import { useVoltarParaHome } from "../nucleo/voltarParaHome";

/**
 * Ativos. A equipe (`interno`) cadastra, edita e vincula; o cliente lê o inventário da própria
 * empresa — sem custo e sem a observação da equipe, que o servidor nem manda.
 */
export function Ativos({ interno = false }: { interno?: boolean }) {
  useTitulo("Ativos");
  const sessao = useSessao();
  const voltarParaHome = useVoltarParaHome();
  const [busca, setBusca] = useState("");
  const [categoria, setCategoria] = useState("");
  const [status, setStatus] = useState("");
  const [organizacaoId, setOrganizacaoId] = useState("");
  const [minhas, setMinhas] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const [modo, setModo] = useState<"ver" | "novo" | "editar">("ver");
  const podeVer = podeNaSessao(sessao.data, "ativo:ver");
  // A lista de organizações é do console: o cliente nem a pede (levaria 403).
  const organizacoes = useOrganizacoesDoCadastro({}, interno && podeVer);
  const ativos = useAtivos(
    { busca, categoria, status, organizacaoId: interno ? organizacaoId : undefined, minhas },
    podeVer,
  );
  const podeGerenciar = interno && podeNaSessao(sessao.data, "ativo:gerenciar");

  if (!sessao.data) {
    return (
      <p className="lead" role="status">
        Verificando o seu acesso…
      </p>
    );
  }
  if (!podeVer) {
    return (
      <Aviso tom="marca" data-testid="ativos-sem-permissao">
        Você não tem acesso ao inventário de ativos.
      </Aviso>
    );
  }

  const caminhoDoChamado = (ticketId: string) =>
    `${interno ? "/admin" : "/app"}/chamados/${encodeURIComponent(ticketId)}`;
  const selecionado = ativos.data?.find((item) => item.id === aberto);

  return (
    <div data-testid="ativos">
      <h1 className="titulo-pagina">Ativos</h1>
      <p className="lead mb-4">
        {interno
          ? "O inventário dos clientes: equipamentos e licenças, quem os usa e em quais chamados aparecem."
          : "Os equipamentos e licenças da sua empresa que a GEOvendas acompanha, e quem usa cada um."}
      </p>
      <div className="grid gap-3 md:grid-cols-4 mb-3">
        <Campo rotulo="Buscar" value={busca} onChange={(e) => setBusca(e.target.value)} data-testid="busca-de-ativos" />
        {interno ? (
          <div className="campo">
            <label className="campo__rotulo" htmlFor="filtro-de-organizacao-dos-ativos">
              Organização
            </label>
            <select
              id="filtro-de-organizacao-dos-ativos"
              className="campo__controle"
              value={organizacaoId}
              onChange={(e) => setOrganizacaoId(e.target.value)}
            >
              <option value="">Todas</option>
              {(organizacoes.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {nomeDaOrganizacao(item)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="campo">
          <label className="campo__rotulo" htmlFor="filtro-de-categoria">
            Categoria
          </label>
          <select
            id="filtro-de-categoria"
            className="campo__controle"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
          >
            <option value="">Todas</option>
            {CATEGORIAS_DE_ATIVO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DA_CATEGORIA[item]}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label className="campo__rotulo" htmlFor="filtro-de-status">
            Status
          </label>
          <select id="filtro-de-status" className="campo__controle" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos</option>
            {STATUS_DE_ATIVO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_STATUS[item]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-4 mb-3">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={minhas} onChange={(e) => setMinhas(e.target.checked)} />
          <span>Só os ativos que eu uso</span>
        </label>
        {podeGerenciar && modo !== "novo" ? (
          <Botao
            onClick={() => {
              setModo("novo");
              setAberto(null);
            }}
            data-testid="novo-ativo"
          >
            Novo ativo
          </Botao>
        ) : null}
      </div>

      {modo === "novo" ? (
        <div className="mb-4">
          <FormularioDeAtivo
            organizacaoInicial={organizacaoId}
            aoSalvar={() => {
              setModo("ver");
              voltarParaHome("Ativo criado.");
            }}
            aoCancelar={() => setModo("ver")}
          />
        </div>
      ) : null}

      {ativos.isPending ? <p role="status">Carregando ativos…</p> : null}
      {ativos.isError ? <Aviso tom="erro">{mensagemDoConsole(ativos.error)}</Aviso> : null}
      {ativos.data && ativos.data.length === 0 ? (
        <Aviso tom="marca" data-testid="sem-ativos">
          Nenhum ativo encontrado.
        </Aviso>
      ) : null}
      {ativos.data && ativos.data.length > 0 ? (
        <Tabela legenda="Ativos, com categoria, número de série, status e garantia.">
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Ativo</CelulaCabecalho>
              {interno ? <CelulaCabecalho>Organização</CelulaCabecalho> : null}
              <CelulaCabecalho>Categoria</CelulaCabecalho>
              <CelulaCabecalho>Série</CelulaCabecalho>
              <CelulaCabecalho>Status</CelulaCabecalho>
              <CelulaCabecalho>Garantia</CelulaCabecalho>
              <CelulaCabecalho>Chamados</CelulaCabecalho>
              <CelulaCabecalho>Ação</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {ativos.data.map((ativo) => (
              <Linha key={ativo.id} data-testid={`ativo-${ativo.nome}`}>
                <Celula>
                  <b>{ativo.nome}</b>
                  <div className="celula-secundaria">
                    {[ativo.fabricante, ativo.modelo].filter(Boolean).join(" ")}
                  </div>
                </Celula>
                {interno ? <Celula>{ativo.organizacao_nome ?? "—"}</Celula> : null}
                <Celula>{ROTULO_DA_CATEGORIA[ativo.categoria]}</Celula>
                <Celula>{ativo.numero_serie ?? "—"}</Celula>
                <Celula>
                  <Chip estado={estadoDoChipDoStatus(ativo.status)}>{ROTULO_DO_STATUS[ativo.status]}</Chip>
                </Celula>
                <Celula>
                  {ativo.garantia_ate ? (
                    <Chip estado={ativo.garantia_vencida ? "atencao" : "ok"}>
                      {dataLegivel(ativo.garantia_ate)}
                    </Chip>
                  ) : (
                    "—"
                  )}
                </Celula>
                <Celula>{ativo.total_de_chamados}</Celula>
                <Celula>
                  <Botao
                    tamanho="pequeno"
                    variante="fantasma"
                    aria-label={`Abrir ${ativo.nome}`}
                    onClick={() => {
                      setAberto(ativo.id);
                      setModo("ver");
                    }}
                  >
                    Abrir
                  </Botao>
                </Celula>
              </Linha>
            ))}
          </Corpo>
        </Tabela>
      ) : null}

      {aberto && selecionado && modo === "ver" ? (
        <div className="mt-4">
          <DetalheDoAtivo
            id={aberto}
            interno={interno}
            podeGerenciar={podeGerenciar}
            aoEditar={() => setModo("editar")}
            caminhoDoChamado={caminhoDoChamado}
          />
        </div>
      ) : null}
      {aberto && selecionado && modo === "editar" ? (
        <div className="mt-4">
          <FormularioDeAtivo
            ativo={selecionado}
            aoSalvar={() => {
              setModo("ver");
              voltarParaHome("Ativo salvo.");
            }}
            aoCancelar={() => setModo("ver")}
          />
        </div>
      ) : null}
    </div>
  );
}
